import axios from "axios";
import { API_BASE } from "../constants/api";
import { AUTH_TOKEN, USER_DATA } from "../constants/localStorageKeys";
import { apiLogger } from "../Utils/logger";
import { retrieve, remove, save } from "../helpers/StorageHelper";
import { getUserTimezone } from "../helpers/TimezoneHelper";
import useAuthStore from "../zustand-stores/AuthStore";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const client = axios.create({
  baseURL: API_BASE,
});

// Request interceptor
client.interceptors.request.use(
  (config) => {
    try {
      const token = retrieve(AUTH_TOKEN);
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add timezone header to all requests
      if (config.headers) {
        config.headers["X-Timezone"] = getUserTimezone();
      }

      // Log API request
      apiLogger.apiRequest(
        config.method?.toUpperCase(),
        config.url,
        config.data,
      );

      return config;
    } catch (error) {
      apiLogger.error("API request interceptor error", error, {
        url: config?.url,
      });
      return Promise.reject(error);
    }
  },
  (error) => {
    apiLogger.error("API request interceptor error", error);
    return Promise.reject(error);
  },
);

// Response interceptor
client.interceptors.response.use(
  (response) => {
    // Log successful API response
    apiLogger.apiResponse(
      response.config?.method?.toUpperCase(),
      response.config?.url,
      response.data,
      response.status,
    );
    return response.data;
  },
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url;
    const method = error.config?.method?.toUpperCase();

    // Log API error
    apiLogger.apiError(method, url, error, status);

    const originalRequest = error.config;

    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return client(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const currentToken = retrieve(AUTH_TOKEN);

      return new Promise((resolve, reject) => {
        axios({
          method: "post",
          url: `${API_BASE}/refresh`,
          data: {
            token: currentToken,
          },
          headers: {
            Authorization: `Bearer ${currentToken}`,
            Accept: "application/json",
          },
        })
          .then((res) => {
            // Check for potential nested data structure in different backend helpers
            const newToken = res.data?.data?.token || res.data?.token;

            if (!newToken) {
              throw new Error("No token found in refresh response");
            }

            save(AUTH_TOKEN, newToken);
            useAuthStore.getState().setAuthSession(newToken);

            // Update Authorization header for the original request
            if (originalRequest.headers.set) {
              originalRequest.headers.set(
                "Authorization",
                `Bearer ${newToken}`,
              );
            } else {
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            }

            processQueue(null, newToken);
            resolve(client(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            apiLogger.warn(
              "Refresh token failed, clearing auth data and redirecting to login",
              { url, method, status },
            );
            // Clear the auth token from localStorage
            remove(AUTH_TOKEN);
            remove(USER_DATA);
            // Redirect to login page
            if (window.location.pathname !== "/login") {
              window.location.href = "/login";
            }
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    if (status === 403) {
      apiLogger.warn(
        "Forbidden access, clearing auth data and redirecting to login",
        { url, method, status },
      );

      // Clear the auth token from localStorage
      remove(AUTH_TOKEN);
      remove(USER_DATA);
      // Redirect to login page
      window.location.href = "/login";
    }

    return Promise.reject(error.response || error.message);
  },
);

// Web helper function
export const web = async (config, data = null) => {
  if (data && config.method?.toLowerCase() === "get") {
    config.params = data;
  } else if (
    data &&
    config.method &&
    ["post", "put", "patch"].includes(config.method.toLowerCase())
  ) {
    config.data = data;
  }

  return await client(config);
};

export default client;
